export type ApiResponseEnvelope<TData = unknown> = {
  success: boolean;
  data: TData;
  message: string;
};

export type RunImmediateJobResponse = ApiResponseEnvelope<unknown>;
export type RunJobResponse = ApiResponseEnvelope<unknown>;

export type DataFlowScheduleItem = {
  data_flow_name: string;
  start_day: number;
  start_month: number;
  start_year: number;
  start_time_hour: number;
  start_time_minutes: number;
  frequency_hours: number;
  frequency_minutes: number;
  frequency_day_of_week: number;
  schedule_type_id: number;
};

export type CreateDataFlowScheduleRequest = {
  job_schedular: DataFlowScheduleItem[];
};

export type CreateDataFlowScheduleResponse = ApiResponseEnvelope<unknown>;

export const UPDATE_DATA_FLOW_BODY_KEYS = [
  'data_flow_name',
  'description',
  'purpose',
  'pipeline_definition',
  'transformation_definition',
  'session_id',
  'status',
  'web_socket_connection_id',
  'step_id',
  'is_active',
  'creator_username_identity',
  'created_datetime_timestamp',
  'dq_failed_records_path',
  'total_dq_passed_records',
  'total_dq_failed_records',
  'data_flow_type',
  'dag_run_id',
  'dag_id',
] as const;

export type UpdateDataFlowBodyKey = (typeof UPDATE_DATA_FLOW_BODY_KEYS)[number];

export type DataFlowType = 'ETL' | 'ML' | 'ADM' | 'MDM' | 'FG' | 'AML';

export type UpdateDataFlowRequest = {
  data_flow_name?: string | null;
  description?: string | null;
  purpose?: string | null;
  pipeline_definition?: unknown;
  transformation_definition?: unknown;
  session_id?: string | null;
  status?: string | null;
  web_socket_connection_id?: string | null;
  step_id?: string | null;
  is_active?: boolean;
  creator_username_identity?: string | null;
  created_datetime_timestamp?: Date | string | null;
  dq_failed_records_path?: string | null;
  total_dq_passed_records?: unknown;
  total_dq_failed_records?: unknown;
  data_flow_type?: DataFlowType | null;
  dag_run_id?: string | null;
  dag_id?: string | null;
};

export type UpdateDataFlowResponseData = {
  data_flow_id: number;
  numUpdatedRows: number;
};

export type UpdateDataFlowResponse = ApiResponseEnvelope<UpdateDataFlowResponseData>;


export type RegisteredTableField = {
  column_name: string;
  data_type: string;
  target_table_column_id: number;
};

export type RegisteredTargetTable = {
  target_table_id: number;
  table_name: string;
  load_type_code: number;
  scd_type_code: number;
  data_source_name: string;
  fields: RegisteredTableField[];
};

export type RegisteredDatabase = {
  type: string;
  source_name: string;
  schema: string;
  database: string;
  secret_key: string;
  image_link: string;
  data_source_type: string;
  data_source_instance_label: string;
  username: string;
  host: string;
  password: string;
  port: string;
};

export type GetRegisteredTablesResponseData = {
  databases: RegisteredDatabase[];
  listOfTargetFiles: RegisteredTargetTable[];
  target_data_source_id: number;
};

export type GetRegisteredTablesResponse =
  ApiResponseEnvelope<GetRegisteredTablesResponseData>;

export type SearchType =
  | 'data_flow'
  | 'source_data_source'
  | 'target_data_source'
  | 'target_table'
  | 'feature_view';

export interface SearchResponse {
  success: boolean;
  message?: string;
}

// 🔹 Field inside file
export interface SourceFileField {
  source_file_field_id: number;
  field_name: string;
  field_data_type: 'string' | 'int' | 'float' | 'date'; // extend if needed
  field_size: number;
  is_business_primary_key: boolean;
}

// 🔹 File info
export interface SourceFile {
  source_file_id: number;
  file_name: string;
  source_file_path: string;
  source_file_type: string;
  authentication_method: string;
  payload_query: string;
  headers: string;
  request_type: string;
  params: string;
  source_file_fields: SourceFileField[];
}

// 🔹 Instance details
export interface InstanceDetails {
  source_data_source_id: number;
  roo_id: number;
  username: string;
  password: string;
  endpoint: string;
  database_name: string;
  data_source_name: string;
  data_source_type: string; // "NO SQL"
  data_source_instance: string; // "mongodb"
  secret_key: string;
  secret_key2: string;
  path: string;
  is_active: boolean;
  created_datetime_timestamp: string;
  last_modified_datetime_timestamp: string;
  bucket_name: string;
  source_files: SourceFile[];
}

export interface InstanceSchemaDataItem {
  instance_details: InstanceDetails[];
}

export interface GetInstanceSchemaResponse {
  success: boolean;
  status: number;
  message: string;
  data: InstanceSchemaDataItem[];
}

export interface NotRegisteredDatabase {
  type: string; // "postgres"
  source_name: string;
  username: string;
  host: string;
  password: string;
  schema: string;
  port: number;
  database: string;
  secret_key: string;
}

export interface NotRegisteredTablesData {
  tables: string[];
  databases: NotRegisteredDatabase[];
}

export interface GetNotRegisteredTablesResponse {
  success: boolean;
  status: number;
  message: string;
  data: NotRegisteredTablesData;
}